import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { CompareContentItem, CompareSettingChange } from "../../types";
import { ChangeStatusLabel } from "../ChangeStatusLabel/ChangeStatusLabel";
import { CompareRow } from "../CompareRow/CompareRow";
import {
  Column,
  Dot,
  Header,
  HeaderAside,
  HeaderSubtitle,
  HeaderTextBlock,
  HeaderTitle,
  List,
  SettingRow,
  SettingsSection,
  SettingsTitle,
} from "./styles";

export interface CompareColumnProps {
  variant: "live" | "changes";
  testId: string;
  headerTitle: ReactNode;
  headerSubtitle?: ReactNode;
  headerAside?: ReactNode;
  items: CompareContentItem[];
  settingChanges?: CompareSettingChange[];
  /** Locates an item's counterpart in the opposite column (GRWBKK-1767). */
  onFindItem?: (id: string) => void;
  /** When set, only these ids get a Find action (rows with a counterpart). */
  findableIds?: Set<string>;
  /** Item id currently highlighted as the find target. */
  highlightedId?: string;
  /** Reports which item is hovered (live column, GRWBKK-1767 linked hover). */
  onHoverItem?: (id: string | undefined) => void;
  /** Item id whose live counterpart is hovered (changes column). */
  linkedHoverId?: string;
}

export const CompareColumn = ({
  variant,
  testId,
  headerTitle,
  headerSubtitle,
  headerAside,
  items,
  settingChanges,
  onFindItem,
  findableIds,
  highlightedId,
  onHoverItem,
  linkedHoverId,
}: CompareColumnProps) => (
  <Column data-testid={testId} $variant={variant}>
    <Header $variant={variant}>
      <Dot $variant={variant} />
      <HeaderTextBlock>
        <HeaderTitle $variant={variant}>{headerTitle}</HeaderTitle>
        {headerSubtitle && <HeaderSubtitle>{headerSubtitle}</HeaderSubtitle>}
      </HeaderTextBlock>
      {headerAside && <HeaderAside>{headerAside}</HeaderAside>}
    </Header>

    <List>
      {items.map((item: CompareContentItem) => (
        <CompareRow
          key={item.id}
          item={item}
          sideKey={variant}
          onFind={
            onFindItem && (!findableIds || findableIds.has(item.id))
              ? () => onFindItem(item.id)
              : undefined
          }
          highlighted={highlightedId === item.id}
          onHoverChange={
            onHoverItem
              ? (hovering) => onHoverItem(hovering ? item.id : undefined)
              : undefined
          }
          linkedHover={linkedHoverId === item.id}
        />
      ))}
    </List>

    {settingChanges && settingChanges.length > 0 && (
      <SettingsSection data-testid={`${testId}-settings`}>
        <SettingsTitle>
          <FormattedMessage
            id="compare_changes.settings.title"
            defaultMessage="Settings"
          />
        </SettingsTitle>
        {settingChanges.map((setting) => (
          <SettingRow
            key={setting.id}
            data-testid={`compare-setting-${setting.id}`}
          >
            <span>{setting.label}</span>
            <ChangeStatusLabel
              changeType={setting.changeType}
              idSuffix={`setting-${setting.id}`}
            />
          </SettingRow>
        ))}
      </SettingsSection>
    )}
  </Column>
);
